/*!

 =========================================================
 * Paper Dashboard Pro Angular - v1.3.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/paper-dashboard-pro-angular
 * Copyright 2018 Creative Tim (http://www.creative-tim.com)

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

 */
import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from 'app/app.module';
import { loadGoogleMaps } from 'app/shared/utils/google-maps-loader';
import { environment } from 'environments/environment';

import { datadogRum } from '@datadog/browser-rum';

if (environment.production) {
	enableProdMode();
}

const datadogInfo = environment.datadogClientToken;

if (datadogInfo) {
	try {
		const buildTracingOrigins = (): string[] => {
			const origins = new Set<string>();
			const addOrigin = (rawUrl?: string) => {
				if (!rawUrl) return;
				try {
					origins.add(new URL(rawUrl).origin);
				} catch {
					// Ignore invalid/empty URLs from environment.
				}
			};

			addOrigin(environment.apiUrl);
			addOrigin(environment.reportingServiceUrl);
			return Array.from(origins);
		};

		datadogRum.init({
			applicationId: environment.datadogApplicationId,
			clientToken: environment.datadogClientToken,
			site: 'us3.datadoghq.com',
			service: environment.datadogService,
			env: environment.name,
			sessionSampleRate: 100,
			sessionReplaySampleRate: 100,
			useSecureSessionCookie: true,
			trackViewsManually: true,
			trackUserInteractions: true,
			trackResources: true,
			trackLongTasks: true,
			version: environment.version,
			defaultPrivacyLevel: 'allow',
			allowedTracingUrls: buildTracingOrigins(),
			proxy: `${environment.apiUrl}dd-proxy`,
			beforeSend: (event) => {
				const errorStringsToIgnore = [
					'AG Grid',
					'ag-grid',
					'evaluation only version',
					'License Key Not Found',
					"Failed to start the transport 'WebSockets'",
					"Failed to start the transport 'ServerSentEvents'",
					'SignalR',
					'WebSocket',
					"Failed to start the transport 'LongPolling'",
					"No Connection with that ID: Status code '404'",
					'make sure you installed the scripts',
					'Error: WebSocket failed to connect',
					'Provided {"error":{},"text":"<!DOCTYPE html><html>',
				];

				const regex = new RegExp(`(${errorStringsToIgnore.join('|')})`, 'i');
				return !(
					event.type === 'error' &&
					(Boolean(regex.test(event.error.message)) || event.error.message.includes('********'))
				);
			},
		});
	} catch (error) {
		console.error('[RUM Init] Failed to initialize Datadog RUM:', error);
	}
}

function loadScript(src: string, integrity?: string): Promise<void> {
	return new Promise((resolve) => {
		const script = document.createElement('script');
		script.src = src;
		if (integrity) {
			script.integrity = integrity;
			script.crossOrigin = 'anonymous';
		}
		script.onload = () => resolve();
		script.onerror = () => {
			console.warn(`[Reveal] Failed to load SDK from ${src}`);
			resolve();
		};
		document.head.appendChild(script);
	});
}

Promise.all([
	loadScript(
		'https://dl.revealbi.io/reveal/libs/1.8.4/infragistics.reveal.js',
		'sha384-iZ4qdGtp0A0ScjQrjTvBOMxKhCxhJ2ycd81pa5PXogxwbBqhAgozmH0zgyeLJhq0'
	),
	loadGoogleMaps(),
])
	.then(() => platformBrowserDynamic().bootstrapModule(AppModule))
	.catch((err) => console.error(err));
